import {
  PropsWithChildren,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useAuth } from 'react-oidc-context';
import { config } from '../../../../common/global/config';
import { useEffectOnce } from '../../../../common/hooks/useEffectOnce';
import { useInactivity } from '../../../../common/hooks/useInactivity';
import { CustomerInfoContext } from '../../contexts/CustomerInfoContext';
import { ContestStatuses } from '../../enums';
import { ContestModel } from '../../models/Contest';
import { ContestHomeDialog } from '../../Pages/HomePage/Contest/ContestHomeDialog';
import { AuthenticationExpiredDialog } from '../dialogs/AuthenticationExpiredDialog';
import { AuthenticationExpiringDialog } from '../dialogs/AuthenticationExpiringDialog';
import { DyConsentDialog } from '../dialogs/DyConsentDialog';
import { logTrace } from '../../../../common/telemetry/logTrace';
import { renewTokenWithRetry } from '../../../../common/auth/renewTokenSilently';

enum GlobalDialogs {
  AuthenticationExpiring = 1,
  AuthenticationExpired = 2,
  Contest = 3,
  DyConsent = 4,
}

export const WithGlobalDialogs = (props: PropsWithChildren<{}>) => {
  const auth = useAuth();
  const getInactivitySeconds = useInactivity();
  const [visibleDialogId, setVisibleDialogId] = useState<GlobalDialogs | null>(
    null,
  );
  const [dialogContest, setDialogContest] = useState<
    ContestModel | undefined
  >();
  const { customerInfo } = useContext(CustomerInfoContext);
  const shouldDelayContestDialogRef = useRef<boolean>(false);
  const renewPromiseRef = useRef<Promise<unknown> | null>(null);
  // Synthetic/monitoring accounts (email prefixed with gappusr_) must not be
  // interrupted by the inactivity-expiring dialog.
  const isGappTestUser = customerInfo?.email?.startsWith('gappusr_') ?? false;

  const loadContests = () => {
    if (!customerInfo?.contests) {
      return;
    }

    const visitedContestIds: number[] = JSON.parse(
      localStorage.getItem('visitedContestIds') ?? '[]',
    );

    const notVisitedContest = customerInfo.contests
      .filter(e => e.status === ContestStatuses.CustomerNotRegistered)
      .find(e => !visitedContestIds.includes(e.contestId));

    if (!notVisitedContest) {
      return;
    }

    const showContestDialog = () => {
      setDialogContest(notVisitedContest);
      setVisibleDialogId(GlobalDialogs.Contest);
    };

    // if there is not already a dialog visible, display the contest dialog
    if (!shouldDelayContestDialogRef.current) {
      showContestDialog();
      return;
    }

    // else show the contest dialog after 5 minutes
    setTimeout(showContestDialog, 5 * 60 * 1000);
  };

  useEffect(() => {
    if (isGappTestUser) {
      return;
    }

    const intervalId = setInterval(() => {
      const inactivitySeconds = getInactivitySeconds();
      if (
        inactivitySeconds > config.idpInactivityWarningSeconds &&
        visibleDialogId === null &&
        auth.user?.profile
      ) {
        logTrace('WithGlobalDialogs: inactivity, showing expiring dialog', {
          inactivitySeconds,
        });
        setVisibleDialogId(GlobalDialogs.AuthenticationExpiring);
      }
    }, 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, [
    visibleDialogId,
    getInactivitySeconds,
    auth.user?.profile,
    isGappTestUser,
  ]);

  useEffect(() => {
    // While the tab is in background the browser throttles timers, so the
    // "expiring" and "expired" events can fire back to back. Both share one
    // silent renew and the expired dialog is shown only if that renew fails;
    // otherwise the dialog would sign out a user whose token was just renewed.
    const trySilentRenew = async () =>
      (await renewTokenWithRetry('WithGlobalDialogs')).user;

    const renewToken = async (trigger: 'expiring' | 'expired') => {
      logTrace('WithGlobalDialogs: access token event', {
        trigger,
        renewInProgress: !!renewPromiseRef.current,
        expiresAt: auth.user?.expires_at,
        hasRefreshToken: !!auth.user?.refresh_token,
      });

      renewPromiseRef.current ??= trySilentRenew().finally(() => {
        renewPromiseRef.current = null;
      });

      const user = await renewPromiseRef.current;
      if (!user) {
        logTrace('WithGlobalDialogs: renew gave up, showing expired dialog', {
          trigger,
        });
        setVisibleDialogId(GlobalDialogs.AuthenticationExpired);
      }
    };

    const handleAccessTokenExpiring = () => renewToken('expiring');
    const handleAccessTokenExpired = () => renewToken('expired');

    auth.events.addAccessTokenExpiring(handleAccessTokenExpiring);
    auth.events.addAccessTokenExpired(handleAccessTokenExpired);

    return () => {
      auth.events.removeAccessTokenExpiring(handleAccessTokenExpiring);
      auth.events.removeAccessTokenExpired(handleAccessTokenExpired);
    };
  }, [auth]);

  useEffectOnce(() => {
    if (customerInfo.allowsDy && customerInfo.showDyConsentPopup) {
      setVisibleDialogId(GlobalDialogs.DyConsent);
      shouldDelayContestDialogRef.current = true;
    }
    loadContests();
  }, []);

  return (
    <>
      {props.children}
      {visibleDialogId === GlobalDialogs.AuthenticationExpiring && (
        <AuthenticationExpiringDialog
          closeDialog={() => setVisibleDialogId(null)}
          showExpiredDialog={() =>
            setVisibleDialogId(GlobalDialogs.AuthenticationExpired)
          }
        />
      )}
      {visibleDialogId === GlobalDialogs.AuthenticationExpired && (
        <AuthenticationExpiredDialog />
      )}
      {visibleDialogId === GlobalDialogs.Contest && dialogContest && (
        <ContestHomeDialog
          contest={dialogContest}
          onCloseDialog={() => {
            setDialogContest(undefined);
            setVisibleDialogId(null);
          }}
        />
      )}
      {visibleDialogId === GlobalDialogs.DyConsent && (
        <DyConsentDialog closeDialog={() => setVisibleDialogId(null)} />
      )}
    </>
  );
};
